import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import { useFeature } from './useFeatures';

const RETRY_MS = 1200;
const MAX_ATTEMPTS = 5;

/**
 * Shared mercy-status source for the MercyBanner (and anything else that must
 * know the safeword floor is active).
 *
 * Fetches the desktop's mercy state via the mercy feature, retrying to ride out
 * the cold-start race, then stays current via 'statusUpdate' pushes and mercy.*
 * Sacred Timeline events. A mercy.activated event is the loudest signal in the
 * system, so we refresh on every mercy.* event.
 *
 * @returns {{ status: Object|null, error: string|null, refresh: Function,
 *            feature: Object|null, hasPeer: boolean }}
 */
export function useMercyStatus() {
    const { connectedPeers } = useWebRTC();
    const feature = useFeature('mercy');
    const sacredFeature = useFeature('sacred-timeline');
    const peerCount = connectedPeers?.length ?? 0;

    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const attemptsRef = useRef(0);
    const timerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const fetchStatus = useCallback(async () => {
        if (!feature) return;
        try {
            const res = await feature.getStatus();
            if (!mountedRef.current) return;
            if (res?.success && res.state) {
                setStatus(res.state);
                setError(null);
                attemptsRef.current = 0;
                return;
            }
            throw new Error(res?.error || 'Mercy status unavailable');
        } catch (err) {
            if (!mountedRef.current) return;
            if (attemptsRef.current < MAX_ATTEMPTS) {
                attemptsRef.current += 1;
                timerRef.current = setTimeout(fetchStatus, RETRY_MS);
            } else {
                setError(err.message || 'Mercy status unavailable');
            }
        }
    }, [feature]);

    useEffect(() => {
        if (!feature || peerCount === 0) return undefined;
        attemptsRef.current = 0;
        fetchStatus();
        const unsub = feature.on('statusUpdate', (state) => {
            setStatus(state);
            setError(null);
        });
        return unsub;
    }, [feature, peerCount, fetchStatus]);

    useEffect(() => {
        if (!sacredFeature) return undefined;
        return sacredFeature.on('sacred_event', (eventData) => {
            const event = eventData?.payload || eventData;
            if (event?.type?.startsWith('mercy.')) fetchStatus();
        });
    }, [sacredFeature, fetchStatus]);

    return { status, error, refresh: fetchStatus, feature, hasPeer: peerCount > 0 };
}
